import React from 'react';
import {
    Badge,
    Button,
    FormGroup,
    FormText,
    Progress,
} from 'reactstrap';
import moment from 'moment';
import { faTasks, faClockRotateLeft, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getTotalHours } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { Can } from '../Can';
import CommonContext, { ApiRoutes, ApplicationPermissions, ChargeTypeResourceTypes } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    FlagIconCellRenderer,
} from '../common/dataGrid/DataGrid';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    PageHeading,
    PageWrap,
    toasty,
    FormLabel,
    onFieldChange,
    SmallButton,
    Textarea,
    Input,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { NotAuthorizedPage } from '../status/StatusCodes';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import { BillingFlags } from '../billing/Billing';
import { ChargeTypeUnits } from '../chargeType/ChargeType';
import BillingChargeEmployeeSlideout from '../billingChargeCategory/BillingChargeEmployeeSlideout';
import InvoiceSlideout from '../invoice/InvoiceSlideout';
import TimePicker from '../common/forms/TimePicker';
import { ModalForm } from '../common/forms/ModalForm';
import { Modal } from '../common/Modal';
import BillingChargeSlideout from './BillingChargeSlideout';

class BillingChargeIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.billingChargeSlideoutRef = React.createRef();
        this.timesheetDetailsRef = React.createRef();
        this.billingChargeEmployeeSlideoutRef = React.createRef();
        this.invoiceSlideoutRef = React.createRef();

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            billingChargeSlideoutOpen: false,
            timesheetDetailsOpen: false,
            selectedDetails: [],
            showRejectBillingChargeModal: false,
            showProcessModal: false,
            isProcessing: false,
            isRejectingBilling: false,
            adminRejecting: false,
            selectedRowRejectionNotes: '',
            loadingReport: false,
            isApproving: false,
            selectedDispatchLocation: null,
            selectedWeekOf: '',
            showAddChargeModal: false,
            isAddingCharge: false,
            isVoidingBilling: false,
            isUnVoidingBilling: false,
            voidNotes: '',
            isAdmin: false,
            isForceReject: false,
            selectedResources: [],
        };

        this.onRowClicked = this.onRowClicked.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onRowSelected = this.onRowSelected.bind(this);
        this.toggleRejectBillingChargeModal = this.toggleRejectBillingChargeModal.bind(this);
        this.toggleVoidBillingChargeModal = this.toggleVoidBillingChargeModal.bind(this);
        this.toggleAddChargeModal = this.toggleAddChargeModal.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }
    onAddCharge = async () => {
        const {
            selectedTimesheet,
            selectedChargeType,
            selectedResources,
            chargeStartTime,
            chargeEndTime,
            totalChargeTime,
            chargeNotes,
            chargeTypesWithResources,
        } = this.state;
        if (
            selectedTimesheet == null
            || selectedChargeType == null
            || (chargeTypesWithResources.includes(selectedChargeType.resourceTypeId) && selectedResources.filter(Boolean).length != selectedChargeType?.numAFADsInSet)
            || totalChargeTime == 0
            || !chargeNotes
            || chargeNotes.length == 0
        ) {
            toasty.error('Error: All fields must have a value');
            return;
        }
        this.setState({ isAddingCharge: true });

        try {
            const data = {
                timesheetId: selectedTimesheet.value,
                chargeTypeId: selectedChargeType.value,
                chargeTypeUnitsId: selectedChargeType.unitsId,
                resourceId: selectedResources.map((x) => x ? x.value : null),
                start: chargeStartTime,
                end: chargeEndTime,
                value: totalChargeTime,
                notes: chargeNotes,
            };

            await httpClient.post(ApiRoutes.billingCharge.add(), data);

            toasty.success('Charge Added.');
            this.state.gridOptions.refresh();
        } catch {
            toasty.error(
                'There was an issue adding the charge record.  Please try your request again or contact support for assistance.',
            );
        } finally {
            this.setState({ isAddingCharge: false, showAddChargeModal: false });
        }
    };

    onApproveButtonClick = async () => {
        this.setState({ isApproving: true });

        const { rowsSelected } = this.state;

        // call accept
        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.billingId);
            const response = await httpClient
                .post(ApiRoutes.billingCharge.approve(), model)
                .catch(this.handleSaveError);

            if (response) {
                toasty.error(`Error approving billing records: ${response}`);
            } else {
                toasty.success('Billing records approved');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({ rowsSelected: [], isApproving: false });
    };

    onBillingChargeEmployeeOpenInvoice = async (id) => {
        if (id) {
            const model = {
                billingIds: null,
                invoiceId: id,
            };

            await this.context.setFormOpened(true);
            this.invoiceSlideoutRef.current.open(model);
        }
    };

    onBillingChargeEmployeeOpenTimesheet = async (timesheetId) => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });

        if (timesheetId) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(timesheetId);
        }
    };

    onBillingChargeEmployeeSlideoutClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        // this.state.gridOptions.refresh();
    };

    onBillingChargeEmployeeSlideoutOpen = async (id) => {
        this.setState({ selectedChargeId: id });

        if (id) {
            await this.context.setFormOpened(true);
            this.billingChargeEmployeeSlideoutRef.current.open(id);
        }
    };

    onBillingChargeSlideoutClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    onBillingChargeSlideoutOpen = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.billingChargeSlideoutRef.current.open(id);
        }
    };

    onChange = onFieldChange;

    onChargeNotesChanged = (ev) => {
        const val = ev.target.value;
        this.setState({ chargeNotes: val });
    };

    onChargeValueChanged = (ev) => {
        const val = ev.target.value;
        this.setState({ totalChargeTime: val });
    };

    onInvoiceSlideoutClosed = async () => {
        const { selectedChargeId } = this.state;

        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });

        this.onBillingChargeEmployeeSlideoutOpen(selectedChargeId);
    };

    onProcessClick = async (values) => {
        const { selectedWeekOf, selectedDispatchLocation } = values;

        if (!selectedWeekOf || !selectedDispatchLocation) return;

        this.setState({ isProcessing: true });

        const model = {
            weekOf: new Date(selectedWeekOf),
            dispatchLocationId: selectedDispatchLocation,
        };

        try {
            const response = await httpClient.post(
                ApiRoutes.billingCharge.process(),
                model,
            );
            if (response) {
                toasty.error(response);
            } else {
                toasty.success('Processing complete');
            }
        } catch {
            toasty.error('Error processing records');
        } finally {
            this.state.gridOptions.refresh();

            this.setState({
                rowsSelected: [],
                isProcessing: false,
                showProcessModal: false,
                selectedWeekOf: '',
                selectedDispatchLocation: null,
            });
        }
    };

    adminReject = async (model) => {
        const response = await httpClient
            .post(ApiRoutes.billingCharge.adminReject(), model)
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Billing: Charges Rejected');
        }

        this.resetRejectState();
    };

    forcedReject = async (model) => {
        const response = await httpClient
            .post(ApiRoutes.billingCharge.forceReject(), model)
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Billing: Charges Rejected');
        }
        this.resetRejectState();
    };

    defaultReject = async (model) => {
        const response = await httpClient
            .post(ApiRoutes.billingCharge.reject(), model)
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Billing: Charges Rejected');
        }
        this.resetRejectState();
    };

    onRejectBilling = async (values, adminRejecting) => {
        const {
            rowsSelected, isForceReject,
        } = this.state;

        const { selectedRowRejectionNotes } = values;

        if (
            selectedRowRejectionNotes.length === 0
            || !selectedRowRejectionNotes.trim()
        ) return;
        this.setState({ isRejectingBilling: true });

        // call reject
        if (rowsSelected) {
            const forcedPermission = this.context.checkUserHasPermission(ApplicationPermissions.billing_charge_force_reject);

            const model = {
                billingId: rowsSelected[0].billingId,
                note: selectedRowRejectionNotes,
            };

            const forceRequired = await httpClient.get(ApiRoutes.billingCharge.needsForceReject(model.billingId))
                .catch(this.handleSaveError);

            if (adminRejecting == true) {
                await this.adminReject(model);
                return;
            }

            if (forcedPermission && forceRequired) {
                if (isForceReject) {
                    await this.forcedReject(model);
                    return;
                } else {
                    // Store the model so continueClicked can call forcedReject directly
                    this.setState({
                        pendingForceRejectModel: model,
                    }, () => {
                        toasty.action(
                            'Unable to Proceed',
                            'Billing Record(s) have saved edits',
                            'Continue Anyway',
                            this.continueClicked,
                            this.resetRejectState,
                        );
                    });
                    return;
                }
            }
            await this.defaultReject(model);
        }
    };

    continueClicked = async () => {
        const { pendingForceRejectModel } = this.state;
        if (pendingForceRejectModel) {
            await this.forcedReject(pendingForceRejectModel);
        }
    };

    resetRejectState = () => {
        this.state.gridOptions.refresh();

        this.setState({
            selectedRow: null,
            showRejectBillingChargeModal: false,
            selectedRowRejectionNotes: '',
            isRejectingBilling: false,
            adminRejecting: false,
            isForceReject: false,
            pendingForceRejectModel: null,
        });
    };

    onRowClicked = (event) => {
        const selection = event.api.getSelectedRows();
        const row = selection.length ? selection[0] : null;

        if (row) {
            this.setState({ selectedRow: row });
            // this.openTimesheetDetails();
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    }

    onSelectedChargeTypeChanged = async (selection) => {
        const { selectedTimesheet } = this.state;
        await this.loadChargeResourcesList(selectedTimesheet, selection);
    };

    onSelectedDispatchLocationChanged = (selection) => {
        const loc = { ...selection };
        this.setState({ selectedDispatchLocation: loc.value });
    };

    onSelectedResourceChanged = (selection, index) => {
        this.setState((s) => {
            const copy = [...s.selectedResources];
            copy[index] = selection ?? null;
            return { selectedResources: copy };
        });
    };

    onSelectedTimesheetChanged = async (selection) => {
        const billingChargeTypes = await httpClient.js(
            ApiRoutes.typeAheads.billingChargeTypes(selection.value),
        );

        this.setState({
            selectedTimesheet: selection,
            billingChargeTypes,
            selectedChargeType: null,
            selectedResources: [],
            billingChargeResources: [],
        });
    };

    onTimeEntryEndChanged = (ev) => {
        const val = ev.target.value;
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        const { chargeStartTime } = { ...this.state };
        const total = getTotalHours(
            chargeStartTime,
            timeValue,
        );

        this.setState({ totalChargeTime: total, chargeEndTime: timeValue });
    };

    onTimeEntryStartChanged = (ev) => {
        const val = ev.target.value;
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        const { chargeEndTime } = { ...this.state };
        const total = getTotalHours(
            timeValue,
            chargeEndTime,
        );

        this.setState({ totalChargeTime: total, chargeStartTime: timeValue });
    };

    onTimesheetDetailsClosed = async () => {
        const { selectedChargeId } = this.state;

        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });

        this.onBillingChargeEmployeeSlideoutOpen(selectedChargeId);
    };

    onUnVoidBilling = async () => {
        const { rowsSelected } = this.state;

        this.setState({ isUnVoidingBilling: true });

        if (rowsSelected) {
            const response = await httpClient
                .js(ApiRoutes.billingCharge.unvoid(rowsSelected[0].billingId))
                .catch(this.handleSaveError);

            if (response != null) {
                toasty.success('Billing: Charges set Pending');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({ rowsSelected: [], isUnVoidingBilling: false });
    };

    onVoidBilling = async ({ voidNotes }) => {
        const { rowsSelected } = this.state;

        // call reject
        if (rowsSelected) {
            const model = {
                billingId: rowsSelected[0].billingId,
                notes: voidNotes,
            };

            const response = await httpClient.post(
                ApiRoutes.billingCharge.void(),
                model,
            );

            if (response) {
                toasty.error(response);
            } else {
                toasty.success('Billing: Charges Voided');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({
            rowsSelected: [],
        });
    };

    downloadExport = async (id) => {
        if (id) {
            this.setState({ loadingReport: true });
            /* var model = { id: id }; */
            await httpClient
                .downloadFile(
                    ApiRoutes.billing.download(id),
                    null,
                    'Billing.pdf',
                )
                .catch(this.handleSaveError);
            this.setState({ loadingReport: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    loadChargeResourcesList = async (selectedTimesheet, selectedChargeType) => {
        let billingChargeResources = [];
        selectedChargeType.numAFADsInSet = selectedChargeType?.numAFADsInSet > 0
            ? selectedChargeType.numAFADsInSet
            : 1;
        if (
            selectedTimesheet
            && selectedChargeType
            && selectedTimesheet.dispatchLocationId > 0
        ) {
            if (selectedChargeType.resourceTypeId == 1) {
                billingChargeResources = await httpClient.js(
                    ApiRoutes.invoice.slideoutStaff(
                        selectedTimesheet.dispatchLocationId,
                    ),
                );
                billingChargeResources = billingChargeResources
                    .filter((x) => Array.isArray(x.groupIds)
                        && x.groupIds.some((grp) => selectedChargeType.eligibleEmployeeGroupIds.includes(Number(grp))))
                    .map((x) => ({ label: x.name, value: x.id }));
            } else {
                billingChargeResources = await httpClient.js(
                    ApiRoutes.invoice.slideoutEquipment(
                        selectedTimesheet.dispatchLocationId,
                    ),
                );
                billingChargeResources = billingChargeResources
                    .filter((x) => selectedChargeType.eligibleEquipmentTypeIds.includes(x.equipmentTypeId))
                    .map((x) => ({ label: x.description, value: x.id }));
            }
        }
        this.setState({
            chargeStartTime: '',
            chargeEndTime: '',
            chargeNotes: '',
            totalChargeTime: 0,
            selectedResources: [],
            selectedTimesheet,
            selectedChargeType,
            billingChargeResources,
        });
    };

    openTimesheetDetails = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    populateState = async () => {
        const { tenantSettings } = this.context.tenant;
        const [
            dispatchLocations,
            billingChargeStatuses,
            flags,
            chargeTypeUnits,
            pendingTimesheets,
        ] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
            httpClient.js(ApiRoutes.typeAheads.billingChargeStatuses()),
            httpClient.js(ApiRoutes.typeAheads.billingChargeFlags()),
            httpClient.js(ApiRoutes.typeAheads.chargeTypeUnits()),
            httpClient.js(ApiRoutes.typeAheads.pendingBillingTimesheets()),
        ]);

        billingChargeStatuses.unshift({
            value: -1,
            label: 'Active',
        });

        const canVoid = this.context.checkUserHasPermission(ApplicationPermissions.billing_void);

        const gridOptions = createGridOptions(this);

        // https://www.ag-grid.com/documentation/javascript/row-selection/#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        gridOptions.rowSelection = 'multiple';

        gridOptions.postProcessData = this.transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;

        gridOptions.rowClassRules = {
            'ag-row-warning': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => (
                    f === BillingFlags.BillNotEqualToCharge
                    || f === BillingFlags.LateArrival
                    || f === BillingFlags.EarlyDeparture
                ));

                return matches.length > 0;
            },
            'ag-row-dst': (params) => ((params.data ?? {}).flag ?? []).includes(BillingFlags.DaylightSavingTime),
            'ag-row-otjobstart': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.OTJobStartRange);

                return matches.length > 0;
            },
            'ag-row-info': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => f === BillingFlags.MultipleJobs);

                return matches.length > 0;
            },
            'ag-row-success': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => (
                    f === BillingFlags.ChargeTypeThresholdExceeded
                    || f === BillingFlags.BreakThresholdExceeded
                ));

                return matches.length > 0;
            },
        };

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            iconRenderer: IconCellRenderer,
            flagIconRenderer: FlagIconCellRenderer,
            variableLinkRenderer: VariableLinkCellRenderer,
        };
        gridOptions.onRowClicked = this.onRowClicked;

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const billingStatusFilterParams = {
            suppressFilterButton: true,
            options: billingChargeStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: -1,
        };

        const flagsFilterParams = {
            suppressFilterButton: true,
            options: flags,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const chargeTypeUnitsFilterParams = {
            suppressFilterButton: true,
            options: chargeTypeUnits,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'Billing.JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
                // floatingFilterComponentParams: {
                //    suppressFilterButton: true,
                // }
            },
            {
                colId: 'Billing.Timesheet.TimesheetNumber',
                headerName: 'Timesheet #',
                field: 'timesheetNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.timesheetId) {
                            return (
                                <Button
                                    color="link"
                                    className="site-link"
                                    disabled={!!this.context.formIsOpen}
                                    title="Open Timesheet"
                                    onClick={() => {
                                        this.openTimesheetDetails(
                                            params.data.timesheetId,
                                        );
                                    }}
                                >
                                    {params.data.timesheetNumber}
                                </Button>
                            );
                        }

                        return params.data.timesheetNumber;
                    }

                    return null;
                },
            },
            {
                colId: 'Billing.Timesheet.Job.Contract.Number',
                headerName: 'Contract Number',
                field: 'contractNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                hide: !tenantSettings?.enableEstimate,
            },
            {
                colId: 'customerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Billing.DispatchLocationId',
                headerName: 'Dispatching',
                field: 'dispatchOfficeName',
                sortable: true,
                flex: 2,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'TimesheetDetail.ResourceName',
                headerName: 'Resource',
                field: 'resourceName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'outOfOrderType',
                minWidth: 85,
                headerName: '',
                sortable: false,
                cellRenderer: (params) => {
                    if (
                        params.data
                        && params.data.resourceTypeId == 1
                        && params.data.employeeHasBillPeriod
                    ) {
                        return (
                            <SmallButton
                                title="View all charges this period"
                                onClick={() => {
                                    this.onBillingChargeEmployeeSlideoutOpen(
                                        params.data.id,
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faClockRotateLeft} />
                            </SmallButton>
                        );
                    }

                    return null;
                },
            },
            {
                colId: 'TimesheetDetail.ChargeTypeName',
                headerName: 'Charge Type',
                field: 'chargeTypeName',
                sortable: true,
                flex: 3,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.hasBreak) {
                            return (
                                <span>
                                    {params.data.chargeTypeName}
                                    <Badge
                                        color="info"
                                        className="ml-2"
                                        style={{
                                            fontSize: '1em',
                                            marginTop: '-2px',
                                        }}
                                        title={`Hours: ${params.data.breakHours}`}
                                    >
                                        B
                                    </Badge>
                                </span>
                            );
                        }

                        return params.data.chargeTypeName;
                    }

                    return null;
                },
            },
            {
                colId: 'Start',
                headerName: 'Start',
                field: 'start',
                sortable: true,
                flex: 1.5,
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'End',
                headerName: 'End',
                field: 'end',
                sortable: true,
                flex: 2,
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'TimesheetDetail.ChargeTypeUnitsId',
                headerName: 'Units',
                field: 'chargeTypeUnitsName',
                minWidth: 105,
                sortable: true,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: chargeTypeUnitsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: chargeTypeUnitsFilterParams,
            },
            {
                colId: 'Charge',
                headerName: 'Charge',
                field: 'charge',
                minWidth: 85,
                sortable: false,
            },
            {
                colId: 'Bill',
                headerName: 'Bill',
                field: 'bill',
                minWidth: 65,
                sortable: false,
            },
            // {
            //    colId: 'Pay',
            //    headerName: 'Pay',
            //    field: 'pay',
            //    minWidth: 65,
            //    sortable: false

            // },
            {
                colId: 'BillingStatusId',
                headerName: 'Status',
                field: 'billingStatus',
                sortable: true,
                flex: 1.5,
                tooltipField: 'status',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billingStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billingStatusFilterParams,
            },
            {
                colId: 'IsEmergency',
                headerName: 'Emergency?',
                field: 'isEmergency',
                sortable: false,
                flex: 1.5,
                cellRenderer: (r) => {
                    if ((r.data ?? {}).isEmergency) {
                        return 'Yes';
                    }

                    return 'No';
                },
            },
            {
                colId: 'Flag',
                headerName: 'Flag',
                field: 'flag',
                flex: 1,
                minWidth: 65,
                sortable: false,
                tooltipValueGetter: ({ value }) => ((value ?? []).length ? `${value.map((f) => f.replace(/\.$/, '')).join(', ')}.` : ''),
                tooltipComponentParams: { color: '#ececec' },
                cellRenderer: 'flagIconRenderer',
                cellRendererParams: {
                    bodyField: 'flag',
                },
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: flagsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: flagsFilterParams,
            },
            {
                colId: 'Id',
                minWidth: 65,
                headerName: 'Edit',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: this.onBillingChargeSlideoutOpen,
                    idField: 'id',
                    iconClass: 'fa-edit billing-edit-btn',
                },
            },
            {
                colId: '',
                headerName: '',
                field: 'selectionPlaceholder',
                sortable: false,
                minWidth: 50,
                checkboxSelection: true,
            },
        ];

        const dataSource = createDataSource(
            ApiRoutes.billingCharge.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            isRejectingTimesheet: false,
            dispatchLocations,
            pendingTimesheets,
            billingChargeTypes: [],
            billingChargeResources: [],
            selectedTimesheet: null,
            selectedChargeType: null,
            isAdmin: this.context?.user?.isAdmin,
            canVoid,
            chargeTypesWithResources: [
                ChargeTypeResourceTypes.Employee,
                ChargeTypeResourceTypes.Equipment,
                ChargeTypeResourceTypes.Rental,
            ],
        });
    };

    toggleAddChargeModal = () => {
        const { showAddChargeModal } = this.state;

        this.setState({
            showAddChargeModal: !showAddChargeModal,
            billingChargeResources: [],
            selectedTimesheet: null,
            selectedChargeType: null,
            selectedResources: [],
            chargeStartTime: 0,
            chargeEndTime: 0,
            totalChargeTime: 0,
            chargeNotes: '',
            billingChargeTypes: [],
        });
    };

    toggleRejectBillingChargeModal = async () => {
        const { rowsSelected } = this.state;

        const adminRequired = await httpClient.get(
            ApiRoutes.billingCharge.needsAdminReject(rowsSelected[0].billingId),
        );

        // if admin not needed just show regular
        if (adminRequired == false) {
            this.showRejectBillingChargeModal(false);
            return;
        }

        // if admin needed and user is not admin
        if (adminRequired == true && !this.context?.user?.isAdmin) {
            toasty.info(
                'An admin needs to reject this timesheet due to it being approved already',
            );
            return;
        }

        // if admin needed and is an admin
        if (adminRequired == true && this.context?.user?.isAdmin) {
            this.showRejectBillingChargeModal(true);
        }
    };

    toggleVoidBillingChargeModal = () => {
        Modal.show({
            title: 'Billing: Void Charges',
            onSubmit: (values) => this.onVoidBilling(values),
            SaveButtonProps: {
                label: 'Void Charges',
                savingLabel: 'Voiding',
                color: 'warning',
            },
            body: () => (
                <>
                    <small
                        style={{ marginBottom: '20px' }}
                        className="form-text text-danger"
                    >
                        This administrative action will void all
                        billing charges with the associated
                        timesheet. Only administrators will be
                        allowed to unvoid and re-approve this
                        voided charges.
                    </small>

                    <Textarea
                        label="Notes"
                        id="voidNotes"
                        name="voidNotes"
                        required
                        placeholder="Enter notes regarding the void."
                        maxLength="500"
                        rows="5"
                        error="Notes are required."
                    />
                </>
            ),
        });
    };

    showRejectBillingChargeModal = (adminRejecting) => {
        Modal.show({
            title: 'Billing: Reject Charges',
            onSubmit: (values) => this.onRejectBilling(values, adminRejecting),
            SaveButtonProps: {
                label: 'Reject',
                savingLabel: 'Rejecting',
                color: 'danger',
                icon: faUndo,
            },
            body: () => (
                <>
                    {adminRejecting ? (
                        <small
                            style={{ marginBottom: '20px' }}
                            className="form-text text-danger"
                        >
                            This administrative action will
                            delete any approved payroll or
                            billing records, and reset the
                            timesheet. Only administrators will
                            be allowed to adjust and re-approve
                            this timesheet. Please take note of
                            Payroll and Billing implications.
                        </small>
                    ) : (
                        <FormText
                            style={{ marginBottom: '20px' }}
                        >
                            This action will reject the
                            timesheet from billing review and
                            remove all charges from payroll
                            review.
                        </FormText>
                    )}

                    <Textarea
                        label="Notes"
                        id="selectedRowRejectionNotes"
                        name="selectedRowRejectionNotes"
                        required
                        placeholder="Enter notes regarding the rejection."
                        maxLength="500"
                        rows="5"
                        error="Notes are required."
                    />
                </>
            ),
        });
    };

    showProcessModal = () => {
        const { dispatchLocations } = this.state;

        Modal.show({
            title: 'Process Approved Charges',
            onSubmit: (values) => this.onProcessClick(values),
            SaveButtonProps: {
                label: 'Process Charges',
                savingLabel: 'Processing',
            },
            body: () => (
                <>
                    <ValidatedSelect
                        placeholder="Select Dispatching"
                        id="selectedDispatchLocation"
                        name="selectedDispatchLocation"
                        label="Dispatching"
                        isClearable={false}
                        options={dispatchLocations}
                        required
                        error="Please select a dispatch location"
                    />
                    <Input
                        label="Week Of"
                        id="weekOf"
                        name="selectedWeekOf"
                        type="date"
                        required
                        error="Please select a date"
                    />
                </>
            ),
        });
    };

    render() {
        const {
            rowData,
            gridOptions,
            isRejectingBilling,
            rowsSelected,
            isApproving,
            isVoidingBilling,
            showAddChargeModal,
            isAddingCharge,
            pendingTimesheets,
            billingChargeTypes,
            billingChargeResources,
            selectedResources,
            selectedChargeType,
            chargeStartTime,
            chargeEndTime,
            totalChargeTime,
            chargeNotes,
            isUnVoidingBilling,
            isAdmin,
            canVoid,
            chargeTypesWithResources,
        } = this.state;

        const { tenantSettings } = this.context.tenant;

        if (this.state.loading || !tenantSettings) {
            return (
                <Progress />
            );
        }

        if (
            !tenantSettings.billingEnabled
            || !tenantSettings.invoicingEnabled
        ) return <NotAuthorizedPage />;
        const numAFADsInSet = selectedChargeType?.numAFADsInSet ?? 1;
        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faTasks}
                        className="mr-2 text-muted"
                    />
                    <span>Billing: Charges</span>
                    <span
                        style={{
                            float: 'right',
                            position: 'relative',
                            top: '-5px',
                        }}
                    >
                        {rowsSelected.length > 1 && (
                            <Button
                                size="sm"
                                color="success"
                                style={{ marginRight: '40px' }}
                                onClick={() => this.onApproveButtonClick()}
                                disabled={
                                    rowsSelected.length === 0
                                    || isApproving
                                }
                            >
                                Approve Selected
                            </Button>
                        )}
                        {rowsSelected.length === 1 && (
                            <>
                                {(
                                    rowsSelected[0] ?? {
                                        billingStatus:
                                            'Not Selected',
                                    }
                                ).billingStatus != 'Approved' &&
                                    (
                                        <Button
                                            size="sm"
                                            color="success"
                                            style={{
                                                marginRight: '10px',
                                            }}
                                            onClick={() => this.onApproveButtonClick()}
                                            disabled={
                                                rowsSelected.length
                                                === 0 || isApproving
                                            }
                                        >
                                            Approve Selected
                                        </Button>
                                    )}
                                <Button
                                    size="sm"
                                    color="danger"
                                    style={{ marginRight: '10px' }}
                                    onClick={() => this.toggleRejectBillingChargeModal()}
                                    disabled={
                                        rowsSelected.length !== 1
                                        || isRejectingBilling
                                    }
                                >
                                    Reject Selected
                                </Button>
                                {(isAdmin || canVoid)
                                    && (
                                        rowsSelected[0] ?? {
                                            billingStatus:
                                                'Not Selected',
                                        }
                                    ).billingStatus != 'Void' && (
                                        <Button
                                            size="sm"
                                            color="warning"
                                            style={{
                                                marginRight: '40px',
                                            }}
                                            onClick={() => this.toggleVoidBillingChargeModal()}
                                            disabled={
                                                rowsSelected.length
                                                !== 1
                                                || isVoidingBilling
                                            }
                                        >
                                            Void Selected
                                        </Button>
                                    )}
                                {(
                                    rowsSelected[0] ?? {
                                        billingStatus:
                                            'Not Selected',
                                    }
                                ).billingStatus == 'Void' &&
                                    (
                                        <Button
                                            size="sm"
                                            color="warning"
                                            style={{
                                                marginRight: '40px',
                                            }}
                                            onClick={() => this.onUnVoidBilling()}
                                            disabled={
                                                rowsSelected.length
                                                !== 1
                                                || isUnVoidingBilling
                                            }
                                        >
                                            Set Selected Pending
                                        </Button>
                                    )}
                            </>
                        )}

                        <Button
                            size="sm"
                            color="success"
                            style={{ marginRight: '10px' }}
                            onClick={() => this.toggleAddChargeModal()}
                            disabled={isApproving}
                        >
                            Add Charge
                        </Button>

                        <Can
                            I="process"
                            a="billing_charge"
                        >
                            <Button
                                size="sm"
                                color="success"
                                onClick={() => this.showProcessModal()}
                            >
                                Process
                            </Button>
                        </Can>
                    </span>
                </PageHeading>

                <DataGridToolbar
                    entity="Billing"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                    serverExport={{
                        apiPath:
                            ApiRoutes.billingCharge.excelExport(),
                        filePrefix: 'BillingChargeSearch',
                    }}
                    serverExportDisabled={
                        !!this.state.saving
                        || !!this.state.loading
                        || !!this.state.loadingData
                    }
                />
                {!!this.state.loadingReport && (
                    <CustomCircularProgress />
                )}

                {!this.state.loadingReport && (
                    <DataGrid
                        domLayout="normal"
                        rowData={rowData}
                        gridOptions={gridOptions}
                        gridStatus={this.state.gridStatus}
                    />
                )}
                <BillingChargeSlideout
                    ref={this.billingChargeSlideoutRef}
                    show={this.state.billingChargeSlideoutOpen}
                    toggleShow={(open) => this.setState({
                        billingChargeSlideoutOpen: open,
                    })}
                    onClose={this.onBillingChargeSlideoutClosed}
                />
                <BillingChargeEmployeeSlideout
                    ref={this.billingChargeEmployeeSlideoutRef}
                    show={
                        this.state.billingChargeEmployeeSlideoutOpen
                    }
                    toggleShow={(open) => this.setState({
                        billingChargeEmployeeSlideoutOpen: open,
                    })}
                    onClose={
                        this.onBillingChargeEmployeeSlideoutClosed
                    }
                    onOpenInvoice={(invoiceId) => {
                        this.onBillingChargeEmployeeOpenInvoice(
                            invoiceId,
                        );
                    }}
                    onOpenTimesheet={(timesheetId) => {
                        this.onBillingChargeEmployeeOpenTimesheet(
                            timesheetId,
                        );
                    }}
                />
                <InvoiceSlideout
                    ref={this.invoiceSlideoutRef}
                    show={this.state.invoiceSlideoutOpen}
                    toggleShow={(open) => this.setState({ invoiceSlideoutOpen: open })}
                    onClose={this.onInvoiceSlideoutClosed}
                />
                <TimesheetDetails
                    ref={this.timesheetDetailsRef}
                    show={this.state.timesheetDetailsOpen}
                    toggleShow={(open) => this.setState({
                        timesheetDetailsOpen: open,
                    })}
                    onClose={this.onTimesheetDetailsClosed}
                    onAccept={this.onAcceptTimesheet}
                    onReject={this.onRejectTimesheet}
                    onlyBillable
                    isBilling
                />

                <ModalForm
                    isOpen={showAddChargeModal}
                    onSubmit={() => this.onAddCharge()}
                    toggle={() => this.toggleAddChargeModal()}
                    title="Add Charge"
                    SaveButtonProps={{
                        label: 'Save',
                        saving: isAddingCharge,
                    }}
                >
                    <ValidatedSelect
                        placeholder="Select Timesheet"
                        id="selectedChargeTimesheet"
                        name="selectedChargeTimesheet"
                        label="Timesheet"
                        isClearable={false}
                        options={pendingTimesheets}
                        value={this.state.selectedTimesheet}
                        required
                        error="You must select a timesheet"
                        onChange={
                            this.onSelectedTimesheetChanged
                        }
                    />

                    <ValidatedSelect
                        placeholder="Select Charge Type"
                        id="selectedChargeType"
                        name="selectedChargeType"
                        label="Charge Type"
                        isClearable={false}
                        value={(
                            billingChargeTypes ?? []
                        ).find(
                            (x) => x.value
                                === selectedChargeType?.value,
                        )}
                        options={billingChargeTypes}
                        onChange={
                            this.onSelectedChargeTypeChanged
                        }
                        required
                        error="You must select a charge type"
                    />

                    {selectedChargeType && chargeTypesWithResources.includes(selectedChargeType.resourceTypeId) && (
                        <FormGroup>
                            <FormLabel
                                htmlFor="selectedChargeResource"
                                text={
                                    selectedChargeType.resourceTypeId === 1
                                        ? 'Employee'
                                        : 'Equipment'
                                }
                            />
                            {billingChargeResources.length > 0 && Array.from(Array(numAFADsInSet).keys()).map((index) => {
                                const resource = selectedResources?.[index] ?? null;
                                const takenValues = (selectedResources ?? [])
                                    .map((r) => r?.value)
                                    .filter((v) => v != null);
                                const adjusted = billingChargeResources.map((item) => {
                                    const isCurrent = resource?.value === item.value;
                                    const isDisabled = !isCurrent && takenValues.includes(item.value);
                                    return { ...item, isDisabled };
                                });
                                return (
                                    <ValidatedSelect
                                        minMenuHeight={100}
                                        maxMenuHeight={200}
                                        name={`selectedResources[${index}]`}
                                        key={`selectedResources[${index}]`}
                                        hideClear
                                        options={adjusted}
                                        getOptionLabel={(
                                            option,
                                        ) => option.label}
                                        getOptionValue={(
                                            option,
                                        ) => option.value}
                                        value={resource ?? null}
                                        onChange={(sel) => this.onSelectedResourceChanged(sel, index)}
                                    />
                                );
                            })}
                        </FormGroup>
                    )}
                    {!!selectedChargeType
                        && selectedChargeType.unitsId
                        == ChargeTypeUnits.Hours && (
                            <>
                                <FormGroup
                                    className="payrollstart"
                                    style={{ width: '60px' }}
                                >
                                    <FormLabel
                                        htmlFor="chargeStartTime"
                                        label="Start"
                                        required
                                    />
                                    <TimePicker
                                        id="chargeStartTime"
                                        required
                                        name="chargeStartTime"
                                        increment={
                                            Number(selectedChargeType?.increment) > 0
                                                ? Number(selectedChargeType.increment)
                                                : 0.25
                                        }
                                        value={chargeStartTime}
                                        onChange={(ev) => this.onTimeEntryStartChanged(
                                            ev,
                                        )}
                                    />
                                </FormGroup>
                                <FormGroup
                                    className="payrollend"
                                    style={{ width: '60px' }}
                                >
                                    <FormLabel
                                        htmlFor="chargeEndTime"
                                        label="End"
                                        required
                                    />
                                    <TimePicker
                                        id="chargeEndTime"
                                        required
                                        name="chargeEndTime"
                                        increment={
                                            Number(selectedChargeType?.increment) > 0
                                                ? Number(selectedChargeType.increment)
                                                : 0.25
                                        }
                                        value={chargeEndTime}
                                        onChange={(ev) => this.onTimeEntryEndChanged(
                                            ev,
                                        )}
                                    />
                                </FormGroup>
                            </>
                        )}
                    <Input
                        label="Charge"
                        id="chargeValue"
                        name="chargeValue"
                        value={totalChargeTime ?? ''}
                        disabled={
                            !!selectedChargeType
                            && selectedChargeType.unitsId
                            == ChargeTypeUnits.Hours
                        }
                        onChange={(ev) => this.onChargeValueChanged(ev)}
                    />
                    <Textarea
                        label="Notes"
                        id="chargeNotes"
                        name="chargeNotes"
                        value={chargeNotes ?? ''}
                        onChange={(ev) => this.onChargeNotesChanged(ev)}
                        placeholder="Enter notes"
                        maxLength="500"
                        rows="5"
                        required
                    />
                </ModalForm>
            </PageWrap>
        );
    }
}

export default BillingChargeIndex;
